/**
 * SEMU Header Controller - Modern 2025 Navigation System
 * Professional UI/UX with consistent interactions
 */

(() => {
if (window.SEMUHeader?.runtimeVersion === 2) {
  window.SEMUHeader.initialize?.(document);
  return;
}

class SEMUHeaderController {
  constructor(root = document) {
    this.root = root;
    this.header = root.querySelector('.header');
    this.navigation = root.querySelector('.header__navigation');
    this.navItems = root.querySelectorAll('.header__nav-item');
    this.megaMenus = root.querySelectorAll('.meganav');
    this.dropdowns = root.querySelectorAll('.header__dropdown');
    this.backdrop = root.querySelector('.header__menu-backdrop');

    this.activeMenu = null;
    this.hoverTimeout = null;
    this.timeoutIds = new Set();
    this.rafIds = new Set();
    this.boundSubDropdowns = new WeakSet();
    this.abortController = new AbortController();
    this.isTouch = 'ontouchstart' in window;
    this.breakpoint = 990; // Changed to match CSS breakpoint

    this.init();
  }

  init() {
    if (!this.header) return;

    this.setupNavigation();
    this.setupAccessibility();
    this.setupEventListeners();

    this.setupSubDropdowns();

    // Add touch class for touch devices
    if (this.isTouch) {
      document.documentElement.classList.add('touch-device');
    }
  }

  setupNavigation() {
    this.navItems.forEach((item, index) => {
      const link = item.querySelector('.header__nav-link');

      // Find mega menu by data-mega-menu-trigger attribute
      const megaMenuTrigger = link?.getAttribute('data-mega-menu-trigger');
      const megaMenu = megaMenuTrigger ? document.getElementById(`mega-menu-${megaMenuTrigger}`) : null;
      const dropdown = item.querySelector('.header__dropdown');
      const menu = megaMenu || dropdown;

      if (!link || !menu) return;

      // Setup ARIA attributes
      const menuId = menu.id || `menu-${index}`;
      menu.id = menuId;
      link.setAttribute('aria-controls', menuId);

      // Desktop hover interactions
      if (!this.isTouch) {
        // Link hover for precise control
        this.listen(link, 'mouseenter', () => {
          if (window.innerWidth >= this.breakpoint) {
            clearTimeout(this.hoverTimeout);
            this.showMenu(item, menu);
          }
        });

        // Item mouseleave for dropdown transition
        this.listen(item, 'mouseleave', () => {
          if (window.innerWidth >= this.breakpoint) {
            this.hoverTimeout = this.setTimer(() => {
              this.hideMenu(menu);
            }, 50); // Reduced from 150ms to 50ms for faster response
          }
        });

        // Mega menu hover (critical for keeping menu open)
        if (megaMenu) {
          this.listen(megaMenu, 'mouseenter', () => {
            if (window.innerWidth >= this.breakpoint) {
              clearTimeout(this.hoverTimeout);
            }
          });

          this.listen(megaMenu, 'mouseleave', () => {
            if (window.innerWidth >= this.breakpoint) {
              this.hoverTimeout = this.setTimer(() => {
                this.hideMenu(menu);
              }, 80); // Reduced from 200ms to 80ms for faster response
            }
          });

          // Also add hover protection for mega menu links
          const megaMenuLinks = megaMenu.querySelectorAll('a');
          megaMenuLinks.forEach((menuLink) => {
            this.listen(menuLink, 'mouseenter', () => {
              clearTimeout(this.hoverTimeout);
            });
          });
        }

        // Dropdown hover (same logic as mega menu for consistency)
        if (dropdown) {
          this.listen(dropdown, 'mouseenter', () => {
            if (window.innerWidth >= this.breakpoint) {
              clearTimeout(this.hoverTimeout);
            }
          });

          this.listen(dropdown, 'mouseleave', () => {
            if (window.innerWidth >= this.breakpoint) {
              this.hoverTimeout = this.setTimer(() => {
                this.hideMenu(menu);
              }, 50); // Reduced from 150ms to 50ms for faster response
            }
          });

          // Also add hover protection for dropdown links
          const dropdownLinks = dropdown.querySelectorAll('a');
          dropdownLinks.forEach((menuLink) => {
            this.listen(menuLink, 'mouseenter', () => {
              clearTimeout(this.hoverTimeout);
            });
          });
        }
      }

      // Click interactions for touch devices and mobile
      this.listen(link, 'click', (e) => {
        if (this.isTouch || window.innerWidth < this.breakpoint) {
          e.preventDefault();
          this.toggleMenu(item, menu);
        }
      });

      // Keyboard navigation
      this.listen(link, 'keydown', (e) => {
        this.handleKeyNavigation(e, item, menu);
      });

      // Setup menu item keyboard navigation
      this.setupMenuKeyNavigation(menu);
    });
  }

  setupSubDropdowns() {
    // Setup sub-dropdown functionality for multi-level menus
    const dropdownItems = this.root.querySelectorAll('.header__dropdown-item--has-children');

    dropdownItems.forEach((item) => {
      if (this.boundSubDropdowns.has(item)) return;
      this.boundSubDropdowns.add(item);

      const link = item.querySelector('.header__dropdown-link');
      const subDropdown = item.querySelector('.header__dropdown--sub');

      if (!link || !subDropdown) {
        console.warn('⚠️ Missing link or sub-dropdown for item:', item);
        return;
      }

      let subHoverTimeout = null;

      // Desktop hover behavior
      if (!this.isTouch) {

        this.listen(item, 'mouseenter', (e) => {
          if (window.innerWidth >= this.breakpoint) {
            clearTimeout(subHoverTimeout);
            subDropdown.classList.add('active');
            subDropdown.setAttribute('aria-hidden', 'false');
            link.setAttribute('aria-expanded', 'true');
          }
        });

        this.listen(item, 'mouseleave', () => {
          if (window.innerWidth >= this.breakpoint) {
            subHoverTimeout = this.setTimer(() => {
              subDropdown.classList.remove('active');
              subDropdown.setAttribute('aria-hidden', 'true');
              link.setAttribute('aria-expanded', 'false');
            }, 30); // Reduced from 150ms to 30ms for instant response
          }
        });

        // Keep menu open when hovering on sub-dropdown
        this.listen(subDropdown, 'mouseenter', () => {
          clearTimeout(subHoverTimeout);
        });

        this.listen(subDropdown, 'mouseleave', () => {
          subHoverTimeout = this.setTimer(() => {
            subDropdown.classList.remove('active');
            subDropdown.setAttribute('aria-hidden', 'true');
            link.setAttribute('aria-expanded', 'false');
          }, 30); // Reduced from 150ms to 30ms for instant response
        });
      }

      // Click behavior for mobile and touch devices
      this.listen(link, 'click', (e) => {
        if (this.isTouch || window.innerWidth < this.breakpoint) {
          if (link.getAttribute('aria-expanded') === 'true') {
            // If submenu is open, allow link navigation
            return;
          }
          e.preventDefault();
          const isActive = subDropdown.classList.contains('active');

          // Close other sub-dropdowns
          this.root.querySelectorAll('.header__dropdown--sub.active').forEach((otherSub) => {
            if (otherSub !== subDropdown) {
              otherSub.classList.remove('active');
              otherSub.setAttribute('aria-hidden', 'true');
              otherSub.parentElement.querySelector('.header__dropdown-link').setAttribute('aria-expanded', 'false');
            }
          });

          // Toggle current sub-dropdown
          subDropdown.classList.toggle('active', !isActive);
          subDropdown.setAttribute('aria-hidden', isActive ? 'true' : 'false');
          link.setAttribute('aria-expanded', isActive ? 'false' : 'true');
        }
      });

      // Keyboard navigation
      this.listen(link, 'keydown', (e) => {
        switch (e.key) {
          case 'ArrowRight':
          case 'Enter':
          case ' ':
            e.preventDefault();
            subDropdown.classList.add('active');
            subDropdown.setAttribute('aria-hidden', 'false');
            link.setAttribute('aria-expanded', 'true');

            // Focus first link in sub-dropdown
            const firstSubLink = subDropdown.querySelector('.header__dropdown-link');
            if (firstSubLink) {
              firstSubLink.focus();
            }
            break;
          case 'Escape':
            subDropdown.classList.remove('active');
            subDropdown.setAttribute('aria-hidden', 'true');
            link.setAttribute('aria-expanded', 'false');
            link.focus();
            break;
        }
      });
    });
  }

  setupAccessibility() {
    // Set initial ARIA states
    this.megaMenus.forEach((menu) => {
      menu.setAttribute('role', 'menu');
      menu.setAttribute('aria-hidden', 'true');
    });

    this.dropdowns.forEach((dropdown) => {
      dropdown.setAttribute('role', 'menu');
      dropdown.setAttribute('aria-hidden', 'true');
    });
  }

  setupEventListeners() {
    // Close menus on outside click
    this.listen(document, 'click', (e) => {
      if (!e.target.closest('.header__navigation')) {
        this.hideAllMenus();
      }
    });

    // Close menus on escape
    this.listen(document, 'keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideAllMenus();
        this.focusActiveNavLink();
      }
    });

    this.listen(
      window,
      'resize',
      this.debounce(() => {
        if (window.innerWidth < this.breakpoint) {
          this.hideAllMenus();
        }
      }, 150),
      { passive: true }
    );

    // Handle backdrop click
    if (this.backdrop) {
      this.listen(this.backdrop, 'click', () => {
        this.hideAllMenus();
      });
    }
  }

  setupMenuKeyNavigation(menu) {
    const menuItems = menu.querySelectorAll('a');

    menuItems.forEach((item, index) => {
      this.listen(item, 'keydown', (e) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            const nextIndex = index < menuItems.length - 1 ? index + 1 : 0;
            menuItems[nextIndex].focus();
            break;

          case 'ArrowUp':
            e.preventDefault();
            const prevIndex = index > 0 ? index - 1 : menuItems.length - 1;
            menuItems[prevIndex].focus();
            break;

          case 'Home':
            e.preventDefault();
            menuItems[0].focus();
            break;

          case 'End':
            e.preventDefault();
            menuItems[menuItems.length - 1].focus();
            break;

          case 'Tab':
            // Allow normal tab behavior but close menu after a delay
            this.setTimer(() => {
              if (!menu.contains(document.activeElement)) {
                this.hideMenu(menu);
              }
            }, 0);
            break;
        }
      });
    });
  }

  handleKeyNavigation(e, navItem, menu) {
    const link = navItem.querySelector('.header__nav-link');

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (menu) {
          e.preventDefault();
          this.toggleMenu(navItem, menu);
        }
        break;

      case 'ArrowDown':
        if (menu) {
          e.preventDefault();
          this.showMenu(navItem, menu);
          this.focusFirstMenuItem(menu);
        }
        break;

      case 'ArrowLeft':
        e.preventDefault();
        this.focusPreviousNavItem(navItem);
        break;

      case 'ArrowRight':
        e.preventDefault();
        this.focusNextNavItem(navItem);
        break;
    }
  }

  showMenu(navItem, menu) {
    // Hide other menus first
    this.hideAllMenus();

    const link = navItem.querySelector('.header__nav-link');

    // Show current menu
    menu.classList.add('active');
    menu.setAttribute('aria-hidden', 'false');
    link.setAttribute('aria-expanded', 'true');

    if (menu.classList.contains('header__dropdown')) {
      this.requestFrame(() => {
        if (!menu.isConnected || !navItem.isConnected) return;
        const header = this.root.querySelector('.header-wrapper');
        if (!header) return;
        const headerBottom = header.getBoundingClientRect().bottom;
        const navItemTop = navItem.getBoundingClientRect().top;
        menu.style.top = `${headerBottom - navItemTop}px`;
      });

    }

    // Show backdrop for mobile
    if (this.backdrop && window.innerWidth < this.breakpoint) {
      this.backdrop.classList.add('header__menu-backdrop--active');
      this.backdrop.setAttribute('aria-hidden', 'false');
    }

    this.activeMenu = menu;
  }

  hideMenu(menu) {
    if (!menu) return;

    // Simple class toggle
    menu.classList.remove('active');
    menu.setAttribute('aria-hidden', 'true');

    // Find trigger
    let trigger;
    if (menu.classList.contains('header__dropdown')) {
      const navItem = menu.closest('.header__nav-item');
      trigger = navItem?.querySelector('.header__nav-link');
      // Reset dropdown position
      menu.style.top = '';
    } else {
      const menuHandle = menu.getAttribute('data-menu-handle');
      trigger = this.root.querySelector(`[data-mega-menu-trigger="${menuHandle}"]`);
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (this.activeMenu === menu) {
      this.activeMenu = null;

      // Hide backdrop
      if (this.backdrop) {
        this.backdrop.classList.remove('header__menu-backdrop--active');
        this.backdrop.setAttribute('aria-hidden', 'true');
      }
    }
  }

  hideAllMenus() {
    [...this.megaMenus, ...this.dropdowns].forEach((menu) => {
      this.hideMenu(menu);
    });

    clearTimeout(this.hoverTimeout);
  }

  toggleMenu(navItem, menu) {
    if (menu.getAttribute('aria-hidden') === 'false') {
      this.hideMenu(menu);
    } else {
      this.showMenu(navItem, menu);
    }
  }

  focusFirstMenuItem(menu) {
    const firstLink = menu.querySelector('a');
    if (firstLink) {
      firstLink.focus();
    }
  }

  focusActiveNavLink() {
    const activeMenu = this.activeMenu;
    if (activeMenu) {
      const navItem = activeMenu.closest('.header__nav-item');
      const link = navItem?.querySelector('.header__nav-link');
      if (link) {
        link.focus();
      }
    }
  }

  focusPreviousNavItem(currentNavItem) {
    const navItems = [...this.navItems];
    const currentIndex = navItems.indexOf(currentNavItem);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : navItems.length - 1;
    const prevLink = navItems[prevIndex]?.querySelector('.header__nav-link');
    if (prevLink) {
      prevLink.focus();
    }
  }

  focusNextNavItem(currentNavItem) {
    const navItems = [...this.navItems];
    const currentIndex = navItems.indexOf(currentNavItem);
    const nextIndex = currentIndex < navItems.length - 1 ? currentIndex + 1 : 0;
    const nextLink = navItems[nextIndex]?.querySelector('.header__nav-link');
    if (nextLink) {
      nextLink.focus();
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);
    this.setTimer(() => announcement.remove(), 1000);
  }

  listen(target, eventName, handler, options = {}) {
    target.addEventListener(eventName, handler, { ...options, signal: this.abortController.signal });
  }

  setTimer(callback, delay) {
    const timerId = window.setTimeout(() => {
      this.timeoutIds.delete(timerId);
      callback();
    }, delay);
    this.timeoutIds.add(timerId);
    return timerId;
  }

  requestFrame(callback) {
    const frameId = requestAnimationFrame(() => {
      this.rafIds.delete(frameId);
      callback();
    });
    this.rafIds.add(frameId);
    return frameId;
  }

  destroy() {
    this.hideAllMenus();
    this.abortController.abort();
    this.timeoutIds.forEach((timerId) => window.clearTimeout(timerId));
    this.rafIds.forEach((frameId) => cancelAnimationFrame(frameId));
    this.timeoutIds.clear();
    this.rafIds.clear();
    this.activeMenu = null;
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = this.setTimer(later, wait);
    };
  }
}

function normalizeHeaderCount(rawCount) {
  const parsedCount = Number.parseInt(rawCount, 10);
  return Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : 0;
}

function renderHeaderCartCount(rawCount, { animate = false } = {}) {
  const itemCount = normalizeHeaderCount(rawCount);
  const displayCount = itemCount < 100 ? String(itemCount) : '99+';

  document.querySelectorAll('[data-header-cart-count]').forEach((counter) => {
    const previousCount = counter.dataset.count;
    counter.textContent = counter.classList.contains('header__cart-counter--text') ? `(${displayCount})` : displayCount;
    counter.dataset.count = displayCount;
    counter.dataset.rawCount = String(itemCount);

    if (animate && itemCount > 0 && previousCount !== displayCount) {
      counter.classList.remove('is-count-updating');
      void counter.offsetWidth;
      counter.classList.add('is-count-updating');
      window.clearTimeout(counter.cartAnimationTimer);
      counter.cartAnimationTimer = window.setTimeout(() => {
        counter.classList.remove('is-count-updating');
      }, 350);
    }
  });

  document.querySelectorAll('[data-cart-icon]').forEach((cartIcon) => {
    const label = cartIcon.dataset.cartLabel || 'Cart';
    cartIcon.setAttribute('aria-label', `${label} (${itemCount})`);
  });
}

// Cart Icon Update Functionality
class SEMUCartController {
  constructor() {
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      this.updateCartIcon(e.detail?.cart || e.detail);
    });
  }

  updateCartIcon(cartData) {
    if (!cartData) return;

    renderHeaderCartCount(cartData.item_count, { animate: true });
  }
}

let activeHeaderController = null;
let cartController = null;

function getHeaderScope(root = document) {
  if (root instanceof Element && root.matches('.section-header')) return root;
  return root.querySelector?.('.section-header') || null;
}

function initializeHeader(root = document) {
  const scope = getHeaderScope(root);
  if (!scope?.querySelector('.header')) return null;

  activeHeaderController?.destroy();
  activeHeaderController = new SEMUHeaderController(scope);
  window.SEMUHeader.headerController = activeHeaderController;
  return activeHeaderController;
}

function initializeControllers() {
  if (!cartController) cartController = new SEMUCartController();
  initializeHeader(document);
}

function handleSectionLoad(event) {
  if (event.target?.querySelector?.('.header')) initializeHeader(event.target);
}

function handleSectionUnload(event) {
  if (!activeHeaderController || !event.target?.contains(activeHeaderController.header)) return;
  activeHeaderController.destroy();
  activeHeaderController = null;
  window.SEMUHeader.headerController = null;
}

window.SEMUHeader = {
  runtimeVersion: 2,
  HeaderController: SEMUHeaderController,
  CartController: SEMUCartController,
  renderCartCount: renderHeaderCartCount,
  initialize: initializeHeader,
  headerController: null,
};

document.addEventListener('shopify:section:load', handleSectionLoad);
document.addEventListener('shopify:section:unload', handleSectionUnload);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeControllers, { once: true });
} else {
  initializeControllers();
}
})();
